<template>
  <b-container :class="classes" fluid>
    <div class="modal">
      <!-- ✕ボタン -->
      <div class="close-button" v-on:click="closePhotoStream">
        <icon-photostream-close-button class="x" width="42" height="42" />
      </div>

      <!-- 画像を並べる -->
      <div class="pt-2 photostream-image-list">
        <div v-for="(file, index) in files" :key="index" class="file">
          <attachment-img
            :image="file"
            :param="param"
            :photostream="true"
            :downloadable="downloadable(file)"
            :isMobile="isMobile"
            :allowSaveAttachments="allowSaveAttachments"
            :clientType="clientType"
          />
        </div>
      </div>
    </div>
  </b-container>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, type PropType } from "vue";
import { Route, NavigationGuardNext } from "vue-router";
import "./photo-stream.scss";
import AttachmentImg from "./AttachmentImg.vue";
import IconPhotostreamCloseButton from "./icon/IconPhotostreamCloseButton.vue";
import type { Attachment } from "../model";
import { CustomFileType } from "@/domain-setting-types";
import { getFileType } from "./file-utility";
import type { ClientType } from "@/store/domain-store";
import type { DownloadDeviceTypes } from "@/suppport-attachment-types";

// PhotoStreamで引数で利用するためのtype
export type PhotoStreamArgs = { files: Attachment[]; messageId: string; commentId: string };

export default defineComponent({
  name: "photo-stream",
  components: {
    AttachmentImg,
    IconPhotostreamCloseButton,
  },
  props: {
    files: { type: Array as PropType<Attachment[]>, default: () => [] },
    param: {
      type: Object as PropType<{ messageId: string; commentId: string }>,
      default: () => ({ messageId: "", commentId: "" }),
    },
    // ダウンロード許可されたファイルの種類 (空=DL禁止)
    downloadableList: { type: Array as PropType<CustomFileType[]>, default: () => [] },
    // モバイルかの判定 (*引数経由でなくstore経由でも可能)
    isMobile: { type: Boolean, default: false },
    clientType: {
      type: [String, Boolean] as PropType<ClientType>,
      default: false as unknown as ClientType,
      required: false,
    },
    allowSaveAttachments: {
      type: Object as PropType<DownloadDeviceTypes>,
      default: () => ({ web: true, ios: true, android: true, desktop: true }),
      required: false,
    },
  },
  setup(props, { emit }) {
    const classes = computed((): { [selector: string]: boolean } => {
      return {
        "photo-stream": true,
      };
    });

    const downloadable = (file: Attachment): boolean => {
      const type = getFileType(file);
      let customFileType: CustomFileType = "image";
      switch (type) {
        case "image": {
          customFileType = "image";
          break;
        }
        case "video": {
          customFileType = "movie";
          break;
        }
        case "pdf": {
          customFileType = "pdf";
          break;
        }
        case "office": {
          customFileType = "office";
          break;
        }
        default: {
          break;
        }
      }
      return props.downloadableList.includes(customFileType);
    };

    const closePhotoStream = (): void => {
      emit("closeModal");
      controlUserScale(false);
    };

    // viewportの制御によるユーザの拡大縮小を制御
    const controlUserScale = (allow: boolean): void => {
      const viewportContent = `width=device-width,initial-scale=1.0,${!allow ? "user-scalable=no" : ""}`;
      document.querySelector("meta[name='viewport']")?.setAttribute("content", viewportContent);
    };

    onBeforeUnmount(() => {
      window.removeEventListener("popstate", closePhotoStream);
    });
    onMounted(() => {
      // ブラウザバックでphotostreamが消えるように
      window.addEventListener("popstate", closePhotoStream);
      controlUserScale(true);
    });

    return {
      classes,
      downloadable,
      closePhotoStream,
      controlUserScale,
    };
  },
  // vue-router の in-component ガード（旧 Component.registerHooks(['beforeRouteUpdate']) 相当）。
  // setup() では宣言できないため Options API のフックとして残す。
  // setup() が返した closePhotoStream はインスタンスに公開されるため this 経由で呼べる。
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): void {
    if (
      from.params.domainId != to.params.domainId ||
      from.params.domainId != to.params.domainId ||
      from.params.topicId != to.params.topicId
    ) {
      // vue-routerでの画面遷移時にphotoStreamを閉じる
      (this as unknown as { closePhotoStream: () => void }).closePhotoStream();
    }
    next();
  },
});
</script>
